// GA4 (Consent Mode v2) + first-touch attribution shared between landing and app via first-party cookies on the root domain.
const GA_ID = process.env.REACT_APP_GA_ID || "";
const ATTR_COOKIE = "basiq_attr";
const VID_COOKIE = "basiq_vid";
const CONSENT_COOKIE = "basiq_consent";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id", "gclid", "fbclid"];
const ATTR_DAYS = 90;

const cookieDomain = () => {
  const h = window.location.hostname.toLowerCase();
  const parts = h.split(".");
  if (/^(\d+\.){3}\d+$/.test(h) || h === "localhost" || parts.length < 2) return "";
  // share across subdomains of a registrable domain (handles .com.br)
  const base = parts.length >= 3 && parts[parts.length - 2].length <= 3 ? parts.slice(-3).join(".") : parts.slice(-2).join(".");
  return base.endsWith("emergentagent.com") ? "" : `.${base}`;
};

const getCookie = (name) => {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : "";
};

const setCookie = (name, value, days) => {
  const d = cookieDomain();
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${days * 86400}; Path=/; SameSite=Lax; Secure${d ? `; Domain=${d}` : ""}`;
};

const uuid = () => (window.crypto?.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`);

export const getVisitorId = () => {
  let v = getCookie(VID_COOKIE);
  if (!v) { v = uuid(); setCookie(VID_COOKIE, v, 400); }
  return v;
};

export const getAttribution = () => {
  try { return JSON.parse(getCookie(ATTR_COOKIE) || "null"); } catch { return null; }
};

export const urlParams = () => {
  const p = new URLSearchParams(window.location.search);
  const out = {};
  UTM_KEYS.forEach((k) => { const v = (p.get(k) || "").trim().slice(0, 120); if (v) out[k] = v; });
  return out;
};

const hasSource = (a) => !!a && ["utm_source", "utm_medium", "utm_campaign", "utm_id", "gclid", "fbclid", "referrer"].some((k) => a[k]);

// First touch wins for 90 days. A plain direct visit is remembered, but it never blocks a later campaign click
// (GA behaves the same: "direct" does not override a known source).
export const captureAttribution = () => {
  const existing = getAttribution();
  if (hasSource(existing)) return existing;
  const utm = urlParams();
  let referrer = "";
  try { const r = document.referrer ? new URL(document.referrer).hostname : ""; if (r && r !== window.location.hostname && !r.endsWith(cookieDomain().replace(/^\./, "") || "\u0000")) referrer = r; } catch {}
  if (!Object.keys(utm).length && !referrer) {
    if (existing) return existing;
    const attr = { referrer: "", landing_path: window.location.pathname, first_seen: new Date().toISOString() };
    setCookie(ATTR_COOKIE, JSON.stringify(attr), ATTR_DAYS);
    return attr;
  }
  const attr = { ...utm, referrer, landing_path: window.location.pathname, first_seen: new Date().toISOString() };
  setCookie(ATTR_COOKIE, JSON.stringify(attr), ATTR_DAYS);
  return attr;
};

export const getConsent = () => getCookie(CONSENT_COOKIE); // "" | "granted" | "denied"

const gtag = (...args) => { window.dataLayer = window.dataLayer || []; window.dataLayer.push(args); };

export const setConsent = (granted) => {
  setCookie(CONSENT_COOKIE, granted ? "granted" : "denied", 180);
  if (!GA_ID) return;
  const v = granted ? "granted" : "denied";
  gtag("consent", "update", { analytics_storage: v, ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied" });
};

let initialized = false;
export const initAnalytics = (site) => {
  if (initialized) return;
  initialized = true;
  captureAttribution();
  getVisitorId();
  if (!GA_ID) return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = gtag;
  gtag("consent", "default", { analytics_storage: getConsent() === "granted" ? "granted" : "denied", ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied", wait_for_update: 500 });
  gtag("js", new Date());
  // the landing is a classic page (auto page_view); the app is a SPA and reports its screens via trackScreen()
  gtag("config", GA_ID, { cookie_domain: cookieDomain().replace(/^\./, "") || "auto", cookie_flags: "SameSite=Lax;Secure", transport_type: "beacon", site_section: site, send_page_view: site === "landing" });
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
};

const attrParams = () => {
  const a = getAttribution() || {};
  return { link_id: a.utm_id || "(none)", campaign_source: a.utm_source || "", campaign_medium: a.utm_medium || "", campaign_name: a.utm_campaign || "" };
};

export const track = (name, params = {}) => {
  if (!GA_ID) return;
  try { gtag("event", name, { ...attrParams(), ...params }); } catch {}
};

// SPA screens → GA4 page_view (one per screen change, never twice in a row for the same screen)
let lastScreen = "";
export const trackScreen = (screen) => {
  if (!GA_ID || !screen || screen === lastScreen) return;
  lastScreen = screen;
  track("page_view", { page_title: `Basiq · ${screen}`, page_location: `${window.location.origin}/#${screen}`, page_path: `/${screen}` });
};

// GA client id (only exists after consent) — lets the backend attribute Stripe-confirmed purchases via Measurement Protocol.
export const getGaClientId = () => {
  const m = getCookie("_ga").match(/GA1\.\d\.(\d+\.\d+)/);
  return m ? m[1] : "";
};

// Internal visit counter (anonymous, deduped per visitor/day/link server-side).
export const pingVisit = async (site) => {
  try {
    const attr = getAttribution() || {};
    const key = `basiq_visit_${site}_${attr.utm_id || "organic"}_${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track/visit`, {
      method: "POST", headers: { "Content-Type": "application/json" }, keepalive: true,
      body: JSON.stringify({ visitor_id: getVisitorId(), site, path: window.location.pathname, attribution: attr, consent: getConsent() === "granted" }),
    });
  } catch {}
};
